import React from "react";

import {
  Box,
  Divider,
  List,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/useAppSelector";
import { canScanQr, isAdmin, isSuperAdmin } from "../../helpers/roles";
import { drawerWidth } from "./Header";
import {
  backToPrevState,
  keepOpenDrawer,
  toggleDrawer,
} from "../../store/drawer.slice";

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
  [theme.breakpoints.up("sm")]: {
    width: 0,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function SideBar() {
  const isMobile = useMediaQuery("(max-width:769px)");
  const auth = useAppSelector((state) => state.auth.user);
  const open = useAppSelector((state) => state.drawer.open);
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (isMobile) {
      dispatch(toggleDrawer());
    }

    if (!isMobile && !open) {
      dispatch(toggleDrawer());
    }
  }, [isMobile]);

  const onSelect = (event) => {
    const route = event?.target?.props?.route;
    if (route) {
      navigate(route);
    }
  };

  const path = location.pathname
    .split("/")
    .filter((d) => d === "" || isNaN(d))
    .join("/");

  // Rol helper'ları backend CustomClaimsPrincipalExtensions ile birebir.
  const rol = auth?.rol;
  const showAdminMenus = isAdmin(rol);
  const showGuestMenus = isSuperAdmin(rol);
  const showQrScanner = canScanQr(rol);

  // Kendo PanelBar: title string olmalı; Link/Fragment icon+text satırını ve selected stilini bozar.
  // PanelBarItem doğrudan çocuk olmalı (<> fragment kullanma).
  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={() => dispatch(keepOpenDrawer())}
      onMouseLeave={() => dispatch(backToPrevState())}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "0px!important",
          ...(!open && {
            visibility: "hidden",
            transition: theme.transitions.create(["visibility"], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
          }),
        }}
      >
        <a className="nav-link" href={"/"}>
          <img
            id="idLogo"
            src="https://www.daikin.com.tr/Content/img/icons/daikin-logo.svg"
            alt=""
          ></img>
        </a>
      </Toolbar>
      <Divider />
      <List component="nav">
        <Box
          className="info"
          sx={{
            ml: 2,
            my: 1,
            ...(!open && {
              visibility: "hidden",
              transition: theme.transitions.create(["visibility"], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            }),
          }}
        >
          <span style={{ fontStyle: "italic" }}>
            {t("welcome")}, <b>{auth?.given_name}</b>
          </span>
        </Box>
        <Divider variant="fullWidth" />

        <PanelBar expandMode={"single"} onSelect={onSelect}>
          {[
            showAdminMenus && (
              <PanelBarItem
                key="home"
                iconClass="fa fa-home fa-5"
                title={t("homepage")}
                selected={path === "/"}
                route="/"
              />
            ),
            showAdminMenus && (
              <PanelBarItem
                key="events"
                iconClass="fa fa-calendar fa-5"
                title={t("events")}
                selected={path === "/events" || path.startsWith("/event")}
                route="/events"
              />
            ),
            showAdminMenus && (
              <PanelBarItem
                key="calendar"
                iconClass="fa fa-calendar fa-5"
                title={t("company_calendar")}
                selected={path === "/calendar"}
                route="/calendar"
              />
            ),
            showAdminMenus && (
              <PanelBarItem
                key="template"
                iconClass="fa fa-envelope fa-5"
                title={t("mail_templates")}
                selected={path === "/template"}
                route="/template"
              />
            ),
            showGuestMenus && (
              <PanelBarItem
                key="guest-events"
                iconClass="fa fa-users fa-5"
                title={t("guest_events")}
                selected={
                  path === "/guest-events" || path.startsWith("/guest-event")
                }
                route="/guest-events"
              />
            ),
            showQrScanner && (
              <PanelBarItem
                key="qr-scanner"
                iconClass="fa fa-qrcode fa-5"
                title={t("qr_scanner")}
                selected={path === "/qr-scanner"}
                route="/qr-scanner"
              />
            ),
          ].filter(Boolean)}
        </PanelBar>
      </List>
    </Drawer>
  );
}
